import { Award, GraduationCap, MapPin, Target, Heart, Users } from "lucide-react";
import { SITE_CONFIG } from "../lib/config";
import { CTASection } from "../components/CTASection";

const VALUES = [
  {
    icon: Target,
    title: "Precisione Tecnica",
    text: "Ogni progetto è studiato nel dettaglio, dal calcolo del fabbisogno alla scelta dei componenti.",
  },
  {
    icon: Heart,
    title: "Trasparenza",
    text: "Preventivi chiari, tempistiche reali e nessuna sorpresa lungo il percorso.",
  },
  {
    icon: Users,
    title: "Vicinanza al Cliente",
    text: "Consigliamo solo aziende installatrici qualificate e con prezzi competitivi.",
  },
];

export default function ChiSiamoPage() {
  return (
    <div data-testid="chi-siamo-page" className="min-h-screen bg-slate-950 text-white">
      {/* HERO */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, rgba(16,185,129,0.18), transparent 50%), radial-gradient(circle at 70% 70%, rgba(20,184,166,0.15), transparent 50%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-xs font-semibold tracking-[0.25em] text-emerald-400 uppercase mb-4">
            Chi siamo
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Un Ingegnere al tuo <span className="text-emerald-400">Fianco</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            Mi chiamo <strong className="text-white">{SITE_CONFIG.fullName}</strong>, sono un Ingegnere
            Elettrico con oltre 10 anni di esperienza nel settore fotovoltaico, Conto Termico e
            progettazione di impianti. Lavoro con sede a {SITE_CONFIG.city} e seguo progetti in
            tutta Italia.
          </p>
        </div>
      </section>

      {/* BIO */}
      <section data-testid="bio-section" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-emerald-500/20">
            <img
              src={SITE_CONFIG.engineerPhoto}
              alt="Ing. Vasta Ivan - foto"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-xs font-semibold tracking-widest text-emerald-400 uppercase mb-1">
                Dott. Ing.
              </div>
              <div className="text-2xl font-bold text-white">Vasta Ivan</div>
              <div className="text-sm text-slate-300">Ingegnere Elettrico</div>
            </div>
          </div>

          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 tracking-tight">
              La mia missione: <span className="text-emerald-400">semplificare la transizione energetica</span>
            </h2>
            <div className="space-y-4 text-base text-slate-300 leading-relaxed">
              <p>
                Da oltre un decennio mi occupo della progettazione di impianti fotovoltaici e della
                gestione completa delle pratiche presso il <strong className="text-white">GSE</strong> e{" "}
                <strong className="text-white">E-Distribuzione</strong>. Conosco a fondo le normative
                e i meccanismi di incentivazione come il <strong className="text-white">Conto Termico 2.0 e 3.0</strong>.
              </p>
              <p>
                Affianco privati, aziende e amministrazioni in tutte le fasi: dall'analisi di
                fattibilità alla progettazione esecutiva, dalla documentazione amministrativa
                all'allaccio in rete. Suggerisco inoltre installatori partner di fiducia con
                prezzi competitivi, così puoi avere un servizio chiavi in mano.
              </p>
              <p>
                Il mio obiettivo è offrirti <strong className="text-emerald-400">trasparenza, qualità
                tecnica e un risparmio reale in bolletta</strong> — partendo da una consulenza
                personalizzata già da 250€.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-sm">
                <GraduationCap className="w-4 h-4" /> Ingegnere Iscritto Albo
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-sm">
                <MapPin className="w-4 h-4" /> {SITE_CONFIG.city}
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-sm">
                <Award className="w-4 h-4" /> 10+ Anni di esperienza
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* VALORI */}
      <section data-testid="valori-section" className="py-24 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-semibold tracking-[0.2em] text-emerald-400 uppercase mb-3">
              I miei valori
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
              Tre cose che <span className="text-emerald-400">non scendono a compromessi</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  data-testid={`valore-card-${i}`}
                  className="p-8 rounded-3xl border border-white/5 bg-white/[0.02] hover:border-emerald-400/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-400">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection title="Vuoi parlare direttamente con un ingegnere?" subtitle="Risposta rapida via WhatsApp, in italiano e senza intermediari." />
    </div>
  );
}
